import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { FORCE_PASSWORD_CHANGE_EVENT } from '@/services/forcePasswordChange';
import { useLogin, useNotify } from 'react-admin';
import { changePassword } from '@/services/manageService/userService';

export default function ForcePasswordChangeDialog() {
    const navigate = useNavigate();
    const location = useLocation();
    const notify = useNotify();
    const login = useLogin();

    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState<string>('Your password is expired or must be changed.');
    const [loginUsername, setLoginUsername] = useState<string>('');
    const [loginOldPassword, setLoginOldPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);

    const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,20}$/;

    const isOnChangePasswordPage = useMemo(() => {
        return location.pathname === '/changePassword' || location.pathname === '/change-password';
    }, [location.pathname]);

    useEffect(() => {
        const handler = (e: Event) => {
            if (isOnChangePasswordPage) {
                return;
            }
            const ce = e as CustomEvent;
            const msg = ce?.detail?.message;
            const u = ce?.detail?.username;
            const p = ce?.detail?.password;
            if (msg && typeof msg === 'string') {
                setMessage(msg);
            } else {
                setMessage('Your password is expired or must be changed.');
            }
            if (u && typeof u === 'string') {
                setLoginUsername(u);
            }
            if (p && typeof p === 'string') {
                setLoginOldPassword(p);
            }
            setNewPassword('');
            setConfirmPassword('');
            setOpen(true);
        };
        window.addEventListener(FORCE_PASSWORD_CHANGE_EVENT, handler as EventListener);
        return () => window.removeEventListener(FORCE_PASSWORD_CHANGE_EVENT, handler as EventListener);
    }, [isOnChangePasswordPage]);

    const onSubmit = async () => {
        if (submitting) return;

        const oldPwd = (loginOldPassword || '').trim();
        const newPwd = (newPassword || '').trim();
        const confirmPwd = (confirmPassword || '').trim();

        if (!oldPwd) {
            notify('Please login again, then change password.', { type: 'warning' });
            setOpen(false);
            navigate('/login');
            return;
        }
        if (!newPwd) {
            notify('Please input new password', { type: 'warning' });
            return;
        }
        if (newPwd !== confirmPwd) {
            notify('Confirm password mismatch', { type: 'warning' });
            return;
        }
        if (!PASSWORD_REGEX.test(newPwd)) {
            notify('Password must contain uppercase, lowercase, number and special character, length 8-20.', { type: 'warning' });
            return;
        }

        setSubmitting(true);
        try {
            await changePassword({ oldPassword: oldPwd, newPassword: newPwd, confirmPassword: confirmPwd });

            setOpen(false);

            try {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('Admin-Token');
                localStorage.removeItem('adminUserInfo');
                localStorage.removeItem('adminPermissions');
            } catch {
                // ignore
            }

            if (loginUsername) {
                await login({ username: loginUsername, password: newPwd } as any);
            } else {
                notify('Password updated. Please login again.', { type: 'info' });
                setOpen(false);
                navigate('/login');
            }
        } catch (err: any) {
            notify(err?.message || 'Change password failed', { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => {}} disableEscapeKeyDown>
            <DialogTitle>Password Change Required</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {message}
                </DialogContentText>
                <Box sx={{ mt: 2 }}>
                    <Stack spacing={2}>
                        <TextField
                            label="New Password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            autoComplete="new-password"
                        />
                        <TextField
                            label="Confirm Password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            fullWidth
                            autoComplete="new-password"
                        />
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onSubmit} variant="contained" disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Update Password & Login'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
